import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  QrCodeIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PrinterIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import apiService from '../../services/api';
import { BoardingLookupResult, Trip } from '../../types';
import { FormCard, Select, Input, DateDisplay } from '../../components/Forms';
import ScanModal from '../../components/Boarding/ScanModal';
import TripPassengerManifest from '../../components/TripPassengerManifest';
import '../../components/TripPassengerManifest/manifest-print.css';
import { useAuth } from '../../contexts/AuthContext';

const statusLabel = (s: string) => {
  if (s === 'boarded') return 'صعد';
  if (s === 'no-show') return 'لم يحضر';
  return 'لم يصعد';
};

const BoardingPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [tripId, setTripId] = useState(searchParams.get('tripId') || '');
  const [busId, setBusId] = useState<string>(searchParams.get('busId') || '');

  const seesAllTrips = ['admin', 'super_admin'].includes(user?.role || '');

  useEffect(() => {
    const fromUrl = searchParams.get('tripId');
    if (fromUrl) setTripId(fromUrl);
    const busFromUrl = searchParams.get('busId');
    if (busFromUrl) setBusId(busFromUrl);
  }, [searchParams]);
  const [lookup, setLookup] = useState<BoardingLookupResult | null>(null);
  const [lookupError, setLookupError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [showManifest, setShowManifest] = useState(false);

  const { data: tripsData, isLoading: tripsLoading } = useQuery({
    queryKey: ['boarding-trips', seesAllTrips],
    queryFn: () =>
      apiService.getTrips(1, 100, seesAllTrips ? {} : { boardingMine: true }),
  });

  const trips = useMemo(() => {
    const list = tripsData?.data || [];
    return list.filter((t: Trip) =>
      ['published', 'full', 'ongoing', 'draft'].includes(t.status || '')
    );
  }, [tripsData]);

  const { data: busesData, refetch: refetchBuses } = useQuery({
    queryKey: ['boarding-buses', tripId],
    queryFn: () => apiService.getBoardingBuses(tripId),
    enabled: !!tripId,
  });

  // Auto-select the only assigned bus for staff
  useEffect(() => {
    const list = busesData?.buses || [];
    if (!tripId || list.length === 0) return;
    if (list.length === 1 && list[0].busId) {
      setBusId(String(list[0].busId));
      return;
    }
    if (busId && !list.some((b) => String(b.busId) === String(busId))) {
      setBusId('');
    }
  }, [busesData, tripId]);

  const { data: manifestData, refetch: refetchManifest } = useQuery({
    queryKey: ['boarding-manifest', tripId, busId],
    queryFn: () => apiService.getBoardingManifest(tripId, busId || null),
    enabled: !!tripId && (seesAllTrips || !!busId),
  });

  const selectedTrip = trips.find((t) => t._id === tripId);

  const handleLookup = useCallback(
    async (rawToken: string) => {
      const token = rawToken.trim();
      if (!token || !tripId) {
        setLookupError('اختر الرحلة وأدخل أو امسح رمز الصعود');
        return;
      }
      if (!seesAllTrips && !busId) {
        setLookupError('اختر الباص المعيّن لك أولاً');
        return;
      }
      setIsLookingUp(true);
      setLookupError('');
      setActionMessage('');
      setLookup(null);
      try {
        const data = await apiService.boardingLookup(token, tripId, busId || null);
        setLookup(data);
        if (data.alreadyBoarded) {
          setActionMessage('تم تسجيل صعود هذا الراكب مسبقاً');
        }
      } catch (err: any) {
        setLookupError(err?.message || 'تعذر العثور على الراكب');
      } finally {
        setIsLookingUp(false);
      }
    },
    [tripId, busId, seesAllTrips]
  );

  const resetLookup = useCallback(() => {
    setLookup(null);
    setActionMessage('');
    setLookupError('');
  }, []);

  const openScan = () => {
    resetLookup();
    setScanOpen(true);
  };

  const closeScan = useCallback(() => {
    setScanOpen(false);
    resetLookup();
  }, [resetLookup]);

  const handleCheckIn = async () => {
    if (!lookup?.token || !tripId) return;
    setIsCheckingIn(true);
    setActionMessage('');
    try {
      const res = await apiService.boardingCheckIn(lookup.token, tripId, busId || null);
      setActionMessage(res.message || 'تم تسجيل الصعود');
      const refreshed = await apiService.boardingLookup(lookup.token, tripId, busId || null);
      setLookup(refreshed);
      refetchBuses();
      refetchManifest();
      queryClient.invalidateQueries({ queryKey: ['boarding-manifest'] });
    } catch (err: any) {
      setLookupError(err?.message || 'فشل تسجيل الصعود');
    } finally {
      setIsCheckingIn(false);
    }
  };

  const handlePrintManifest = async () => {
    await refetchManifest();
    setShowManifest(true);
    setTimeout(() => window.print(), 400);
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto pb-12">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <QrCodeIcon className="w-7 h-7 text-primary-600" />
            بوابة الصعود
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            اختر الرحلة والباص، امسح QR الراكب، ثم أكّد الصعود
          </p>
        </div>
        <button
          type="button"
          onClick={handlePrintManifest}
          disabled={!tripId}
          className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          <PrinterIcon className="w-4 h-4" />
          طباعة كشف الباص
        </button>
      </div>

      <FormCard title="مسح رمز الصعود">
        <div className="flex flex-col items-center gap-3 py-4 text-center">
          <p className="text-sm text-gray-600">
            {tripId
              ? 'اضغط الزر وصوّر QR الراكب — تظهر بياناته مباشرة مع زر تأكيد الصعود'
              : 'اختر الرحلة من الأسفل أولاً لتفعيل المسح'}
          </p>
          <button
            type="button"
            disabled={!tripId}
            onClick={openScan}
            className="btn-primary px-6 py-3 text-base"
          >
            <QrCodeIcon className="h-5 w-5" />
            مسح QR
          </button>
        </div>
      </FormCard>

      <FormCard title="اختيار الرحلة والباص">
        {!tripsLoading && trips.length === 0 && (
          <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            {seesAllTrips
              ? 'لا توجد رحلات متاحة حالياً.'
              : 'لا توجد رحلات معيّنة لك. تواصل مع المدير لتعيين باص لك من نموذج الرحلة.'}
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الرحلة</label>
            <Select
              value={tripId}
              onChange={(e) => {
                setTripId(e.target.value);
                setBusId('');
                setLookup(null);
                setLookupError('');
              }}
              options={[
                { value: '', label: tripsLoading ? 'جاري التحميل...' : 'اختر رحلة' },
                ...trips.map((t) => ({
                  value: t._id,
                  label: `${t.tripNumber} — ${t.status || ''}`,
                })),
              ]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الباص</label>
            <Select
              value={busId}
              onChange={(e) => {
                setBusId(e.target.value);
                setLookup(null);
              }}
              disabled={!tripId}
              options={[
                {
                  value: '',
                  label: !tripId
                    ? 'اختر الرحلة أولاً'
                    : seesAllTrips
                      ? 'كل الركاب / بدون تحديد'
                      : 'اختر الباص المعيّن لك',
                },
                ...(busesData?.buses || [])
                  .filter((b) => b.busId)
                  .map((b) => ({
                    value: String(b.busId),
                    label: `${b.busNumber || 'باص'} ${b.plateNumber ? `(${b.plateNumber})` : ''} — ${b.boarded}/${b.total} صعد`,
                  })),
              ]}
            />
            {tripId && !seesAllTrips && (busesData?.buses || []).length === 0 && (
              <p className="mt-2 text-xs text-red-600">
                لا يوجد باص معيّن لك في هذه الرحلة.
              </p>
            )}
          </div>
        </div>

        {busesData && (
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-lg bg-gray-50 p-3">
              <div className="text-xs text-gray-500">الإجمالي</div>
              <div className="text-xl font-bold text-gray-900">
                {busId
                  ? (busesData.buses || []).find((b) => String(b.busId) === String(busId))?.total ||
                    (busesData.buses || []).reduce((s, b) => s + (b.total || 0), 0)
                  : (busesData.buses || []).reduce((s, b) => s + (b.total || 0), 0)}
              </div>
            </div>
            <div className="rounded-lg bg-green-50 p-3">
              <div className="text-xs text-green-700">صعد</div>
              <div className="text-xl font-bold text-green-700">
                {busId
                  ? (busesData.buses || []).find((b) => String(b.busId) === String(busId))?.boarded || 0
                  : (busesData.buses || []).reduce((s, b) => s + (b.boarded || 0), 0)}
              </div>
            </div>
            <div className="rounded-lg bg-amber-50 p-3">
              <div className="text-xs text-amber-700">متبقي</div>
              <div className="text-xl font-bold text-amber-700">
                {busId
                  ? (busesData.buses || []).find((b) => String(b.busId) === String(busId))?.remaining || 0
                  : (busesData.buses || []).reduce((s, b) => s + (b.remaining || 0), 0)}
              </div>
            </div>
          </div>
        )}
      </FormCard>

      {tripId && (
        <FormCard title="قائمة ركاب الرحلة">
          {!manifestData ? (
            <p className="text-sm text-gray-500">جاري تحميل الركاب...</p>
          ) : (manifestData.passengers || []).length === 0 ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
              لا يوجد ركاب مرتبطون بهذه الرحلة بعد. تأكد أن الحجز مربوط بنفس الرحلة، ثم حدّث الصفحة.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-right text-gray-500 border-b">
                    <th className="py-2 px-2">#</th>
                    <th className="py-2 px-2">المقعد</th>
                    <th className="py-2 px-2">الاسم</th>
                    <th className="py-2 px-2">الحجز</th>
                    <th className="py-2 px-2">الحالة</th>
                  </tr>
                </thead>
                <tbody>
                  {manifestData.passengers.map((p) => (
                    <tr key={`${p.bookingNumber}-${p.seatNumber}-${p.index}`} className="border-b border-gray-100">
                      <td className="py-2 px-2">{p.index}</td>
                      <td className="py-2 px-2 font-medium">{p.seatNumber || '—'}</td>
                      <td className="py-2 px-2">{p.name}</td>
                      <td className="py-2 px-2">{p.bookingNumber}</td>
                      <td
                        className={`py-2 px-2 font-medium ${
                          p.boardingStatus === 'boarded' ? 'text-green-700' : 'text-amber-700'
                        }`}
                      >
                        {statusLabel(p.boardingStatus)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </FormCard>
      )}

      <ScanModal
        open={scanOpen}
        onClose={closeScan}
        title="مسح رمز الصعود"
        subtitle="وجّه الكاميرا على QR الراكب"
        elementId="boarding-qr-reader"
        onLookup={handleLookup}
        isLookingUp={isLookingUp}
        error={lookupError}
        hasResult={!!lookup}
        onReset={resetLookup}
      >
        {lookup && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">الاسم</span>
                <p className="font-semibold text-gray-900 text-lg">{lookup.passenger.name}</p>
              </div>
              <div>
                <span className="text-gray-500">المقعد</span>
                <p className="font-semibold text-primary-700 text-lg">
                  {lookup.passenger.seatNumber || '—'}
                </p>
              </div>
              <div>
                <span className="text-gray-500">رقم الحجز</span>
                <p className="font-medium">{lookup.booking.bookingNumber}</p>
              </div>
              <div>
                <span className="text-gray-500">الهوية</span>
                <p className="font-medium">{lookup.passenger.idNumber || '—'}</p>
              </div>
              <div>
                <span className="text-gray-500">الجوال</span>
                <p className="font-medium">{lookup.passenger.phone || '—'}</p>
              </div>
              <div>
                <span className="text-gray-500">حالة الصعود</span>
                <p
                  className={`font-semibold ${
                    lookup.boardingStatus === 'boarded' ? 'text-green-700' : 'text-amber-700'
                  }`}
                >
                  {statusLabel(lookup.boardingStatus)}
                </p>
              </div>
              {(lookup.housing?.hotelName ||
                lookup.housing?.roomNumber ||
                lookup.passenger.roomNumber) && (
                <>
                  <div>
                    <span className="text-gray-500">الفندق</span>
                    <p className="font-medium">{lookup.housing?.hotelName || '—'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">رقم الغرفة</span>
                    <p className="font-semibold text-primary-700 text-lg">
                      {lookup.passenger.roomNumber || lookup.housing?.roomNumber || '—'}
                    </p>
                  </div>
                  {lookup.housing?.locationText && (
                    <div className="sm:col-span-2">
                      <span className="text-gray-500">موقع السكن</span>
                      <p className="font-medium">{lookup.housing.locationText}</p>
                    </div>
                  )}
                  {lookup.housing?.mapsUrl && (
                    <div className="sm:col-span-2">
                      <a
                        href={lookup.housing.mapsUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-600 underline text-sm"
                      >
                        فتح الموقع على الخريطة
                      </a>
                    </div>
                  )}
                </>
              )}
              {lookup.trip.route && (
                <div className="sm:col-span-2">
                  <span className="text-gray-500">المسار</span>
                  <p className="font-medium">
                    {lookup.trip.route.from} ← {lookup.trip.route.to}
                    {lookup.trip.route.dateFrom && (
                      <>
                        {' '}
                        — <DateDisplay value={lookup.trip.route.dateFrom} className="inline-flex" />
                      </>
                    )}
                  </p>
                </div>
              )}
              <div>
                <span className="text-gray-500">الدفع</span>
                <p className="font-medium">{lookup.booking.paymentStatus}</p>
              </div>
            </div>

            {lookupError && (
              <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <ExclamationTriangleIcon className="h-5 w-5 shrink-0" />
                <span>{lookupError}</span>
              </div>
            )}
            {actionMessage && !lookupError && (
              <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
                <CheckCircleIcon className="h-5 w-5 shrink-0" />
                <span>{actionMessage}</span>
              </div>
            )}

            <button
              type="button"
              disabled={isCheckingIn || lookup.boardingStatus === 'boarded'}
              onClick={handleCheckIn}
              className="btn-success w-full py-3 text-base"
            >
              <UserGroupIcon className="h-5 w-5" />
              {lookup.boardingStatus === 'boarded'
                ? 'مسجّل مسبقاً'
                : isCheckingIn
                  ? 'جاري التسجيل...'
                  : 'تأكيد الصعود'}
            </button>
          </div>
        )}
      </ScanModal>

      {/* Print host — portal to body so sidebar/layout are not printed */}
      {showManifest &&
        selectedTrip &&
        createPortal(
          <div className="manifest-print-root fixed inset-0 z-50 overflow-y-auto bg-white">
            <div className="no-print sticky top-0 bg-white border-b p-3 flex justify-between items-center">
              <span className="font-medium">معاينة كشف الباص</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm"
                >
                  طباعة
                </button>
                <button
                  type="button"
                  onClick={() => setShowManifest(false)}
                  className="px-3 py-1.5 rounded border text-sm"
                >
                  إغلاق
                </button>
              </div>
            </div>
            <div className="manifest-scroll">
              <TripPassengerManifest
                trip={selectedTrip}
                bookings={[]}
                boardingManifest={manifestData || undefined}
                busFilterId={busId || null}
              />
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default BoardingPage;
